// frontend/src/components/ActionPlans/ActionPlanDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { actionPlansAPI, usersAPI, ap_areasAPI, ap_subAreasAPI, ap_categoriesAPI, attachmentsAPI } from '../../api/client';
import { usePermissions } from '../../context/PermissionsContext';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import FileUpload from '../Common/FileUpload';
import RichTextEditor from '../Common/RichTextEditor';
import SearchableSelect from '../Common/SearchableSelect';
import {
  FiSave, FiArrowLeft, FiPlus, FiTrash2, FiEdit2,
  FiCheckCircle, FiChevronRight, FiChevronDown,
  FiCalendar, FiLock, FiGlobe, FiFileText, FiMessageSquare, FiClock
} from 'react-icons/fi';
import './ActionPlan.css';
import { AP_STATUS_META, ACTION_STATUS_META, statusPillStyle } from '../../constants/statusMeta';
import AuditLogPanel from '../Common/AuditLog/AuditLogPanel';
import ActionPlanTaskModal from './ActionPlanTaskModal';
import '../Settings/Modal.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const PLAN_TYPES = [
  { value: 'improvement', label: 'Melhoria Contínua',  color: '#10b981', icon: '🔄' },
  { value: 'corrective',  label: 'Ação Corretiva',      color: '#ef4444', icon: '🔧' },
  { value: 'preventive',  label: 'Ação Preventiva',     color: '#f59e0b', icon: '🛡️' },
  { value: 'project',     label: 'Projeto',             color: '#6366f1', icon: '📁' },
  { value: 'incident',    label: 'Incidente',           color: '#dc2626', icon: '⚠️' },
];
const STATUSES     = ['NEW','IN_REVIEW','IN_PROGRESS','ON_HOLD','COMPLETED','CANCELLED'];
const ALL_STATUSES = STATUSES;
const PRIORITY_OPTIONS = [
  { value:'LOW',      label:'Baixa'   },
  { value:'MEDIUM',   label:'Média'   },
  { value:'HIGH',     label:'Alta'    },
  { value:'CRITICAL', label:'Crítica' },
];
const ACTION_STATUSES = ['NEW','IN_REVIEW','IN_PROGRESS','ON_HOLD','COMPLETED','CANCELLED'];


// ── Status Stepper ─────────────────────────────────────────────────────────────
// STATUSES for stepper = the 5 main flow steps (CANCELLED handled separately)
const FLOW_STEPS = ['NEW','IN_REVIEW','IN_PROGRESS','ON_HOLD','COMPLETED'];

const StatusStepper = ({ status, onChange, canEdit }) => {
  const isCancelled = status === 'CANCELLED';
  const currentIdx = FLOW_STEPS.indexOf(status);

  return (
    <div className="ap-stepper-wrap">
      <div className="ap-stepper">
        {FLOW_STEPS.map((s, i) => {
          const m = AP_STATUS_META[s];
          const isActive = !isCancelled && i <= currentIdx;
          const isCurrent = s === status;

          return (
            <React.Fragment key={s}>
              <button
                type="button"
                className={[
                  'ap-step',
                  isActive ? 'is-active' : '',
                  isCurrent ? 'is-current' : '',
                  isCancelled ? 'is-disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => canEdit && !isCancelled && onChange(s)}
                disabled={!canEdit || isCancelled}
              >
                <span
                  className="ap-step-circle"
                  style={{
                    background: isActive ? m.color : 'var(--gray-200)',
                    borderColor: isCurrent ? m.color : 'transparent',
                    boxShadow: isCurrent ? `0 0 0 3px ${m.color}22` : 'none',
                  }}
                >
                  {isActive && i < currentIdx ? (
                    <FiCheckCircle size={11} color="white" />
                  ) : (
                    <span className="ap-step-index" style={{ color: isActive ? 'white' : 'var(--gray-400)' }}>
                      {i + 1}
                    </span>
                  )}
                </span>
                <span className="ap-step-label" style={{ color: isActive ? m.color : 'var(--gray-400)' }}>
                  {m.label}
                </span>
              </button>

              {i < FLOW_STEPS.length - 1 && (
                <div
                  className="ap-step-connector"
                  style={{
                    background: !isCancelled && i < currentIdx
                      ? AP_STATUS_META[FLOW_STEPS[i + 1]].color
                      : 'var(--gray-200)',
                  }}
                />
              )}



            </React.Fragment>
          );
        })}

        {canEdit && !isCancelled && currentIdx < FLOW_STEPS.length - 1 && (
          <button
            type="button"
            className="btn btn-primary btn-sm ap-stepper-next"
            onClick={() => onChange(FLOW_STEPS[currentIdx + 1])}
          >
            <FiChevronRight size={12} />
            {AP_STATUS_META[FLOW_STEPS[currentIdx + 1]].label}
          </button>
        )}


  {isCancelled && (
          <div className="ap-stepper-cancelled">
            <span className="ap-stepper-cancelled-badge">✕ Cancelado</span>
            {canEdit && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange('NEW')}>
                Reabrir
              </button>
            )}
          </div>
        )}

        {canEdit && !isCancelled && status !== 'COMPLETED' && (
          <button type="button" className="ap-stepper-cancel-link" onClick={() => onChange('CANCELLED')}>
            Cancelar plano
          </button>
        )}

      </div>



    </div>
  );
};

// ── Multi-user chips ───────────────────────────────────────────────────────────
const MultiUserSelect = ({ selectedIds = [], onChange, users, excludeId }) => {
  const available = users.filter(u =>
    !selectedIds.map(Number).includes(Number(u.user_id))
    && Number(u.user_id) !== Number(excludeId)
  );
  const selected = users.filter(u => selectedIds.map(Number).includes(Number(u.user_id)));
  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6 }}>
          {selected.map(u => (
            <span key={u.user_id} className="user-chip">
              {u.full_name}
              <button type="button"
                onClick={() => onChange(selectedIds.filter(x => Number(x) !== Number(u.user_id)))}
                style={{ background:'none', border:'none', cursor:'pointer',
                  color:'var(--primary)', padding:'0 0 0 4px', fontWeight:700, lineHeight:1 }}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <div style={{ maxWidth: 220 }}>
          <SearchableSelect value="" onChange={id => { if (id) onChange([...selectedIds, Number(id)]); }}
            options={available} getOptionLabel={u => u.full_name} getOptionValue={u => u.user_id}
            placeholder="+ Adicionar"/>
        </div>
      )}
    </div>
  );
};



// ─── CompletionCombo — combobox with 0-100 in 5% steps ───────────────────────
const COMPLETION_OPTS = Array.from({ length: 21 }, (_, i) => ({ value: i * 5, label: `${i * 5}%` }));

const CompletionCombo = ({ value, onChange, className = '' }) => (
  <select
    className={`form-control ap-completion-select ${className}`.trim()}
    value={value ?? 0}
    onChange={e => onChange(parseInt(e.target.value))}
  >
    {COMPLETION_OPTS.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

// ─── CausaRaizPanel — collapsed by default ────────────────────────────────────
const CausaRaizPanel = ({ rootCause, onRootCauseChange, pendingFiles, onPendingFilesChange, planId, isNew }) => {
  const [open, setOpen] = React.useState(false);
  const hasContent = rootCause && rootCause.replace(/<[^>]*>/g,'').trim().length > 0;
  return (
    <div style={{ marginTop:20, borderTop:'2px solid var(--gray-100)', paddingTop:12 }}>
      <button type="button"
        onClick={() => setOpen(o => !o)}
        style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none',
          cursor:'pointer', padding:0, fontSize:12, fontWeight:700, color:'var(--gray-500)',
          textTransform:'uppercase', letterSpacing:'0.4px' }}>
        {open ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}
        Causa Raiz &amp; Anexos
        {hasContent && (
          <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--primary)',
            display:'inline-block', marginLeft:2 }}/>
        )}
        {pendingFiles?.length > 0 && (
          <span style={{ fontSize:11, background:'#e0f2f1', color:'var(--primary)',
            padding:'1px 7px', borderRadius:10 }}>
            {pendingFiles.length} ficheiro{pendingFiles.length > 1 ? 's' : ''}
          </span>
        )}
      </button>
      {open && (
        <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:16 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Causa Raiz</label>
            <RichTextEditor
              value={rootCause}
              onChange={onRootCauseChange}
              placeholder="Porquê de ter acontecido? (análise 5 porquês, diagrama Ishikawa…)"
              minHeight={110}
            />
          </div>
          <div>
            <label className="form-label" style={{ marginBottom:8, display:'block' }}>
              Anexos
              {pendingFiles?.length > 0 && (
                <span style={{ fontSize:11, fontWeight:600, color:'var(--primary)',
                  background:'#e0f2f1', padding:'2px 8px', borderRadius:10, marginLeft:8 }}>
                  {pendingFiles.length} pendente{pendingFiles.length > 1 ? 's' : ''}
                </span>
              )}
            </label>
            {isNew && (
              <p style={{ fontSize:12, color:'var(--gray-400)', marginBottom:8 }}>
                Os ficheiros serão carregados quando guardar o plano.
              </p>
            )}
            <FileUpload entityType="action_plan"
              entityId={planId}
              pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange}
              sideBySide={true} label="" maxFiles={20}/>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionPlanDetail = () => {
  const { publicId } = useParams();
  const routeLocation = useLocation();
  const isNew = !publicId || publicId === 'new';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toasts, removeToast, toast_success, toast_error, toast_warning_confirm } = useToast();
  const { can } = usePermissions();

  const [plan,       setPlan]       = useState(null);
  const [users,      setUsers]      = useState([]);
  const [areas,      setAreas]      = useState([]);
  const [subAreas,   setSubAreas]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(!isNew);
  const [saving,     setSaving]     = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditAction,  setAuditAction]  = useState(null);
  const [tab,        setTab]        = useState('detail');
  const [pendingFiles,       setPendingFiles]       = useState([]);
  const [pendingActionFiles, setPendingActionFiles] = useState({}); // { [action.public_id]: File[] }
  const [taskModal,          setTaskModal]          = useState(null); // null=closed | {}=new | {...action}=edit
  const [newUpdate,    setNewUpdate]    = useState('');
  const [addingUpdate, setAddingUpdate] = useState(false);

  const emptyForm = {
    title:'', area:'', problem_description:'', root_cause:'',
    current_situation:'', follow_up_notes:'', completion: 0,
    identified_date: new Date().toISOString().slice(0,10),
    target_date:'', priority:'MEDIUM', plan_type:'improvement', status:'NEW',
    owner_user_id:null, co_responsible_ids:[], is_public:true,
    effectiveness_eval:'', is_effective:null, completed_date:'', lessons_learned:'',
  };
  const [form, setForm] = useState(emptyForm);
  const setF = (k, v) => setForm(prev => {
    const next = { ...prev, [k]: v };

    // Auto-derive status from completion / completed_date
    // Only promote NEW/IN_PROGRESS -- never override WAITING/CLOSED/CANCELLED
    const promotable = ['NEW', 'IN_PROGRESS', 'ANALYSIS'];

    if (k === 'completion' || k === 'completed_date') {
      const completion    = k === 'completion'     ? v : next.completion;
      const completedDate = k === 'completed_date' ? v : next.completed_date;

      if ((completion === 100 || completedDate) && promotable.includes(next.status)) {
        next.status = 'COMPLETED';
        // Auto-fill completed_date with today if not set
        if (!next.completed_date)
          next.completed_date = new Date().toISOString().slice(0, 10);
      } else if (completion > 0 && completion < 100 && next.status === 'NEW') {
        next.status = 'IN_PROGRESS';
      }
    }

    return next;
  });

  // ── Load lookup data ────────────────────────────────────────────────────────
  useEffect(() => {
    usersAPI.getAll().then(r => {
      if (r.data.success) setUsers(r.data.users || r.data.data || []);
    }).catch(() => {});
    ap_areasAPI.getAll().then(r => {
      if (r.data.success) setAreas(r.data.data || []);
    }).catch(() => {});
    ap_subAreasAPI.getAll().then(r => {
      if (r.data.success) setSubAreas(r.data.data || []);
    }).catch(() => {});
    ap_categoriesAPI.getAll().then(r => {
      if (r.data.success) setCategories(r.data.data || []);
    }).catch(() => {});
  }, []);

  // ── Load plan ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    try {
      const r = await actionPlansAPI.getById(publicId);
      if (r.data.success) {
        const p = r.data.data;
        setPlan(p);
        setForm({
          title:               p.title || '',
          area:                p.area  || '',
          problem_description: p.problem_description || '',
          area_id:             p.area_id    || null,
          sub_area_id:         p.sub_area_id || null,
          category_id:         p.category_id || null,
          root_cause:          p.root_cause || '',
          current_situation:   p.current_situation || '',
          completion:          p.completion ?? 0,
          follow_up_notes:     p.follow_up_notes || '',
          identified_date:     p.identified_date?.slice(0,10) || '',
          target_date:         p.target_date?.slice(0,10) || '',
          priority:            p.priority    || 'MEDIUM',
          plan_type:           p.plan_type   || 'improvement',
          status:              p.status      || 'NEW',
          owner_user_id:       p.owner_user_id || null,
          is_public:           p.is_public !== false,
          effectiveness_eval:  p.effectiveness_eval || '',
          is_effective:        p.is_effective ?? null,
          completed_date:      p.completed_date?.slice(0,10) || '',
          lessons_learned:     p.lessons_learned || '',
          co_responsible_ids:  (p.members||[])
            .filter(m => m.role==='CO_RESPONSIBLE').map(m => Number(m.user_id)),
        });
      }
    } catch { toast_error(t('messages.error_load','Erro ao carregar')); }
    finally  { setLoading(false); }
  }, [publicId, isNew]);

  const [exportingReport, setExportingReport] = useState(false);

  useEffect(() => { load(); }, [load]);

  // ── File uploads ────────────────────────────────────────────────────────────
  const uploadPendingFiles = async (planId, referenceNo) => {
    if (!pendingFiles.length) return;
    try {
      await attachmentsAPI.upload('action_plan', planId, pendingFiles, '', '', referenceNo || '');
      setPendingFiles([]);
    } catch (err) {
      console.error('Plan file upload error:', err);
      toast_error('Plano guardado mas alguns ficheiros falharam ao carregar');
    }
  };

  const uploadPendingActionFiles = async (actionKey, actionId) => {
    const files = pendingActionFiles[actionKey];
    if (!files?.length) return;
    try {
      await attachmentsAPI.upload('action_plan_action', actionId, files, '', '', '');
      setPendingActionFiles(prev => { const n = {...prev}; delete n[actionKey]; return n; });
    } catch (err) {
      console.error('Action file upload error:', err);
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim()) { toast_error('Título obrigatório'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await actionPlansAPI.create(form);
        if (r.data.success) {
          const np = r.data.data;
          await uploadPendingFiles(np.action_plan_id, np.reference_no);
          toast_success('Guardado');
          navigate(`/action-plans/${np.public_id}`, { replace: true });
        }
      } else {
        const r = await actionPlansAPI.update(publicId, form);
        if (r.data.success) {
          await uploadPendingFiles(plan.action_plan_id, plan.reference_no);
          toast_success('Guardado');
          await load();
        }
      }
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleTaskModalSave = async (form) => {
    const isNew = !form.public_id;
    if (isNew) {
      const r = await actionPlansAPI.addAction(publicId, form);
      if (r.data?.success && pendingActionFiles['new']?.length) {
        await uploadPendingActionFiles('new', r.data.data.action_id);
      }
      // If plan is NEW and an action was started, promote plan to IN_PROGRESS
      if (plan?.status === 'NEW') {
        setForm(f => ({ ...f, status: 'IN_PROGRESS' }));
        // Only send the plan fields — never spread the task form into a plan update
        await actionPlansAPI.update(publicId, { status: 'IN_PROGRESS' });
      }
      toast_success('Tarefa adicionada');
    } else {
      await actionPlansAPI.updateAction(form.public_id, {
        description:         form.description,
        responsible_user_id: form.responsible_user_id,
        target_date:         form.target_date,
        execution_date:      form.execution_date,
        status:              form.status,
        result_notes:        form.result_notes,
        progress_notes:      form.progress_notes,
        completion:          form.completion ?? 0,
      });
      if (pendingActionFiles[form.public_id]?.length) {
        await uploadPendingActionFiles(form.public_id, form.action_id);
      }
      toast_success('Tarefa guardada');
    }
    setTaskModal(null);
    await load();
  };

  const handleDeleteAction = (action) => {
    toast_warning_confirm(
      'Eliminar tarefa?',
      async () => {
        try {
          await actionPlansAPI.deleteAction(action.public_id);
          toast_success('Tarefa eliminada');
          await load();
        } catch { toast_error('Erro ao eliminar'); }
      }
    );
  };

  // ── Comments ────────────────────────────────────────────────────────────────
  const handleAddUpdate = async () => {
    if (!newUpdate.trim()) return;
    setAddingUpdate(true);
    try {
      await actionPlansAPI.addUpdate(publicId, { update_text: newUpdate });
      setNewUpdate('');
      await load();
      toast_success('Comentário adicionado');
    } catch { toast_error('Erro'); }
    finally { setAddingUpdate(false); }
  };

  // ── Render guards ───────────────────────────────────────────────────────────
  if (loading) return (
    <div className="page-container" style={{ paddingTop: 0 }}>
      <div className="loading">A carregar…</div>
    </div>
  );

  const actions    = plan?.actions || [];
  const updates    = plan?.updates || [];
  const doneCount  = actions.filter(a => a.status==='DONE').length;
  const typeInfo   = PLAN_TYPES.find(t => t.value === form.plan_type) || PLAN_TYPES[0];
  const statusInfo = AP_STATUS_META[form.status] || AP_STATUS_META.NEW;
  const isClosed   = ['COMPLETED','CANCELLED'].includes(form.status);

  const TABS = [
    { key:'detail',     label:'Detalhes' },
    { key:'conclusion', label:'Conclusão' },
    { key:'comments',   label:`Comentários (${updates.length})` },
  ];


  // Add handler:
  const handleDownloadReport = async () => {
    try {
      setExportingReport(true);
      const response = await actionPlansAPI.downloadReport(publicId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlanoMelhoria_${form.reference_no || publicId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast_error('Erro ao gerar relatório');
    } finally {
      setExportingReport(false);
    }
  };

  // ── JSX ─────────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="ap-header-bar">
        {/* Left: back + identity */}
        <div className="ap-header-left">
          <button className="btn btn-secondary btn-sm"
            onClick={() => navigate('/action-plans', {
              state: routeLocation.state?.aplFilters
                ? { aplFilters: routeLocation.state.aplFilters } : undefined
            })}
            style={{ flexShrink:0 }}>
            <FiArrowLeft size={14} />
          </button>
          <div className="ap-header-identity">
            <div className="ap-header-chips">
              <span className="ap-ref-chip">{plan?.reference_no || 'Novo'}</span>
              <span className="ap-type-chip" style={{ background:`${typeInfo.color}15`, color:typeInfo.color, border:`1px solid ${typeInfo.color}40` }}>
                {typeInfo.icon} {typeInfo.label}
              </span>
              {!isNew && (
                <span className="ap-status-chip"
                  style={{ background:statusInfo.bg, color:statusInfo.color, borderColor:`${statusInfo.color}40` }}>
                  {statusInfo.label}
                </span>
              )}
              {form.priority && (
                <span className="ap-priority-chip">
                  {PRIORITY_OPTIONS.find(p => p.value === form.priority)?.label || form.priority}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="ap-header-actions">
          {!isNew && (
<button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleDownloadReport}
              disabled={exportingReport}
              title="Gerar relatório PDF"
            >
              <FiFileText size={14} />
              {exportingReport ? 'A gerar…' : 'Relatório PDF'}
            </button>
          )}
          {!isNew && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowAuditLog(true)}
              title="Ver histórico de alterações"
            >
              <FiClock size={14} />
              Histórico
            </button>
          )}
          <button
            type="button"
            className={`ap-visibility-toggle ${form.is_public ? 'is-public' : 'is-private'}`}
            onClick={() => setF('is_public', !form.is_public)}
          >
            {form.is_public ? <FiGlobe size={13} /> : <FiLock size={13} />}
            {form.is_public ? 'Público' : 'Privado'}
          </button>
          {can('action_plans', isNew ? 'create' : 'write') && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <FiSave size={14} />
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
          )}
        </div>
      </div>

      {/* ── Status Stepper ─────────────────────────────────────────────── */}
      <div className="ap-stepper-bar ap-stepper-bar--compact">
        <StatusStepper
          status={form.status}
          onChange={v => setF('status', v)}
          canEdit={can('action_plans', 'write') && form.status !== 'COMPLETED'}
        />
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      {!isNew && (
        <div className="ap-tabs">
          {TABS.map(t_ => {
            const active = tab === t_.key;
            return (
              <button
                key={t_.key}
                type="button"
                onClick={() => setTab(t_.key)}
                className={`ap-tab ${active ? 'active' : ''}`}
                style={active ? { color: statusInfo.color, borderColor: `${statusInfo.color}33`, boxShadow: `inset 0 0 0 1px ${statusInfo.color}22` } : undefined}
              >
                {t_.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ══ DETALHES ═════════════════════════════════════════════════════ */}
      {(isNew || tab==='detail') && (
        <div className="form-section">
          <div className="form-grid ap-detail-grid">


            {/* Type */}
            <div className="form-group ap-span-full">
              <label className="form-label">Tipo</label>
              <div className="ap-type-row">
                {PLAN_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`ap-type-option ${form.plan_type === type.value ? 'active' : ''}`}
                    onClick={() => setF('plan_type', type.value)}
                    style={{
                      borderColor: form.plan_type === type.value ? type.color : 'var(--gray-200)',
                      background: form.plan_type === type.value ? `${type.color}15` : 'white',
                      color: form.plan_type === type.value ? type.color : 'var(--gray-600)',
                    }}
                  >
                    {type.icon} {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div className="form-group ap-compact-field">
              <label className="form-label">Prioridade</label>
                <SearchableSelect
                  value={form.priority}
                  onChange={v => setF('priority', v)}
                  options={PRIORITY_OPTIONS}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => o.value}
                  placeholder="— prioridade —"
                />
            </div>

            {/* Status override */}
            {!isNew && (
              <div className="form-group ap-compact-field">
                <label className="form-label">Estado</label>
                  <SearchableSelect
                    value={form.status}
                    onChange={v => setF('status', v)}
                    options={ALL_STATUSES.map(s => ({ value: s, label: AP_STATUS_META[s]?.label || s }))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    placeholder="— estado —"
                  />
              </div>
            )}

            {/* Area */}
            <div className="form-group ap-compact-field">
              <label className="form-label">Área</label>
                <SearchableSelect
                  value={form.area_id}
                  onChange={v => {
                    const id = v ? Number(v) : null;
                    setF('area_id', id);
                    setF('area', areas.find(a => a.area_id === id)?.area_name || '');
                    setF('sub_area_id', null);
                  }}
                  options={areas}
                  getOptionLabel={a => a.area_name}
                  getOptionValue={a => a.area_id}
                  placeholder="— selecionar área —"
                />
            </div>

            {/* Sub-area */}
            <div className="form-group ap-compact-field">
              <label className="form-label">Sub-Área</label>
                <SearchableSelect
                  value={form.sub_area_id}
                  onChange={v => setF('sub_area_id', v ? Number(v) : null)}
                  options={form.area_id ? subAreas.filter(s => s.area_id === form.area_id) : subAreas}
                  getOptionLabel={s => s.sub_area_name}
                  getOptionValue={s => s.sub_area_id}
                  placeholder={form.area_id ? '— selecionar —' : '— selecionar área primeiro —'}
                />
            </div>

            {/* Category */}
            <div className="form-group ap-compact-field">
              <label className="form-label">Categoria</label>
                <SearchableSelect
                  value={form.category_id}
                  onChange={v => setF('category_id', v ? Number(v) : null)}
                  options={categories}
                  getOptionLabel={c => c.category_name}
                  getOptionValue={c => c.category_id}
                  placeholder="— selecionar —"
                />
            </div>

            {/* Identified date */}
            <div className="form-group ap-compact-field ap-field-xs">
              <label className="form-label">Data de Identificação</label>
              <input type="date" className="form-control ap-date-input-sm" value={form.identified_date}
                onChange={e => setF('identified_date', e.target.value)}/>
            </div>

            {/* Target date */}
            <div className="form-group ap-compact-field ap-field-xs">
              <label className="form-label"><FiCalendar size={12}/> Data Prazo Previsto</label>
              <input type="date" className="form-control ap-date-input-sm" value={form.target_date}
                onChange={e => setF('target_date', e.target.value)}/>
            </div>

                        {/* Completion % */}
            <div className="form-group ap-compact-field ap-field-xxs">
              <label className="form-label">Conclusão %</label>
              <CompletionCombo value={form.completion ?? 0} onChange={v => setF('completion', v)} />
            </div>         

            {/* Owner */}
            <div className="form-group ap-compact-field">
              <label className="form-label">Responsável</label>
                <SearchableSelect
                  value={form.owner_user_id}
                  onChange={v => setF('owner_user_id', v ? Number(v) : null)}
                  options={users}
                  getOptionLabel={u => u.full_name}
                  getOptionValue={u => u.user_id}
                  placeholder="— selecionar —"
                />
            </div>


            {/* Co-responsible */}
            <div className="form-group ap-compact-field ap-field-wide">
              <label className="form-label">Co-responsável(is)</label>
              <MultiUserSelect selectedIds={form.co_responsible_ids}
                onChange={ids => setF('co_responsible_ids', ids)}
                users={users} excludeId={form.owner_user_id}/>
            </div>


            {/* Title */}
            <div className="form-group ap-span-full">
              <label className="form-label">Título / Problema *</label>
              <textarea value={form.title}
                onChange={e => setF('title', e.target.value)}
                placeholder="Descreva o problema ou título do plano…"
                maxLength={300} rows={2}
                className="form-control ap-title-textarea"/>
            </div>

   

            {/* Descrição do Problema */}
            <div className="form-group ap-span-full">
              <label className="form-label">Descrição do Problema</label>
              <RichTextEditor
                value={form.problem_description || ''}
                onChange={html => setF('problem_description', html)}
                placeholder="O que aconteceu? (sintomas, contexto, impacto, quando foi detetado)"
                minHeight={180}
              />
            </div>

          </div>{/* end form-grid */}

          {/* ── SEPARATOR + TAREFAS ──────────────────────────────────── */}
          <div style={{ margin:'24px 0 0', borderTop:'2px solid var(--gray-100)', paddingTop:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:12 }}>
              <span style={{ fontSize:13, fontWeight:700, color:'var(--gray-600)',
                textTransform:'uppercase', letterSpacing:'0.5px' }}>
                Tarefas ({actions.length}){actions.length > 0 && ` · ${doneCount} concluídas`}
              </span>
              {can('action_plans', 'create') && (
                <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => setTaskModal({})}>
                  <FiPlus size={13}/> Nova Tarefa
                </button>
              )}
            </div>

            {actions.length === 0 ? (
              <p style={{ fontSize:12, color:'var(--gray-400)', fontStyle:'italic', padding:'8px 0' }}>
                Sem tarefas definidas.
              </p>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--gray-200)' }}>
                    {['#','Titulo','Responsável','Prazo','Data Conclusão','Estado','Conclusão',''].map((h,i) => (
                      <th key={i} style={{ padding:'6px 10px', textAlign:'left', fontSize:11,
                        fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase',
                        letterSpacing:'0.4px', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {actions.map((action, idx) => {
                    const isDone = action.status === 'DONE';
                    return (
                      <tr key={action.public_id || idx}
                        onClick={() => setTaskModal({ ...action })}
                        style={{ borderBottom:'1px solid var(--gray-100)', cursor:'pointer',
                          background: isDone ? '#f0fdf4' : 'white' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDone ? '#dcfce7' : '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = isDone ? '#f0fdf4' : 'white'}
                      >
                        <td style={{ padding:'8px 10px', color:'var(--gray-400)', fontWeight:600, width:32 }}>
                          {action.sequence_no || idx+1}
                        </td>
                        <td style={{ padding:'8px 10px', maxWidth:260 }}>
                          <div style={{ fontWeight:500,
                            textDecoration: isDone ? 'line-through' : 'none',
                            color: isDone ? 'var(--gray-400)' : 'var(--gray-900)',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                            title={action.title?.replace(/<[^>]*>/g,'')}>
                            {action.title?.replace(/<[^>]*>/g,'') || '—'}
                          </div>
                        </td>
                        <td style={{ padding:'8px 10px', color:'var(--gray-600)', whiteSpace:'nowrap' }}>
                          {action.responsible_name || <span style={{ color:'var(--gray-300)' }}>—</span>}
                        </td>
                        <td style={{ padding:'8px 10px', whiteSpace:'nowrap',
                          color: action.target_date && new Date(action.target_date) < new Date() && !isDone
                            ? '#dc2626' : 'var(--gray-600)' }}>
                          {action.target_date
                            ? new Date(action.target_date).toLocaleDateString('pt-PT')
                            : <span style={{ color:'var(--gray-300)' }}>—</span>}
                        </td>
                        <td style={{ padding:'8px 10px', whiteSpace:'nowrap', color:'var(--gray-500)', fontSize:12 }}>
                          {action.execution_date
                            ? new Date(action.execution_date).toLocaleDateString('pt-PT')
                            : <span style={{ color:'var(--gray-300)' }}>—</span>}
                        </td>
                        <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>
                          {(() => {
                            const am = ACTION_STATUS_META[action.status] || ACTION_STATUS_META.NEW;
                            return (
                              <span className={`status-badge status--${(action.status||'new').toLowerCase()}`}
                                style={{ fontSize:11 }}>
                                {am.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>
                          <span style={{ fontSize:12, fontWeight:700,
                            color: (action.completion ?? 0) >= 100 ? '#059669' : (action.completion ?? 0) > 0 ? 'var(--primary)' : 'var(--gray-500)' }}>
                            {action.completion ?? 0}%
                          </span>
                        </td>
                        <td style={{ padding:'8px 6px', textAlign:'right', whiteSpace:'nowrap' }}>
                          <div style={{ display:'flex', gap:2 }}>
                            <button type="button"
                              onClick={e => { e.stopPropagation(); setAuditAction(action); }}
                              title="Histórico da tarefa"
                              style={{ background:'none', border:'none', cursor:'pointer',
                                color:'var(--gray-500)', padding:'2px 4px' }}>
                              <FiClock size={13}/>
                            </button>
                            {can('action_plans', 'delete') && action.public_id && (
                              <button type="button"
                                onClick={e => { e.stopPropagation(); handleDeleteAction(action); }}
                                style={{ background:'none', border:'none', cursor:'pointer',
                                  color:'var(--gray-500)', padding:'2px 4px' }}>
                                <FiTrash2 size={13}/>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── CAUSA RAIZ + ANEXOS — collapsible ────────────────── */}
          <CausaRaizPanel
            rootCause={form.root_cause || ''}
            onRootCauseChange={html => setF('root_cause', html)}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
            planId={isNew ? null : plan?.action_plan_id}
            isNew={isNew}
          />

          {/* Meta */}
          {!isNew && plan && (
            <div style={{ fontSize:12, color:'var(--gray-500)', background:'var(--gray-50)',
              padding:'8px 12px', borderRadius:6, display:'flex', gap:20,
              flexWrap:'wrap', marginTop:12 }}>
              <span>Criado por: <strong>{plan.created_by_name}</strong></span>
              <span>Em: <strong>{new Date(plan.created_at).toLocaleString('pt-PT')}</strong></span>
              {plan.updated_by_name && (
                <span>Atualizado por: <strong>{plan.updated_by_name}</strong></span>
              )}
            </div>
          )}
        </div>
      )}

      {!isNew && tab==='comments' && (
        <div className="form-section">
          {can('action_plans', 'write') && (
            <div style={{ marginBottom:20 }}>
              <label className="form-label">Novo Comentário</label>
              <RichTextEditor
                value={newUpdate}
                onChange={html => setNewUpdate(html)}
                placeholder="Ponto de situação, decisão tomada, observação…"
                minHeight={100}
              />
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleAddUpdate}
                  disabled={addingUpdate || !(newUpdate.replace(/<[^>]*>/g,'').trim())}>
                  {addingUpdate ? 'A guardar…' : <><FiMessageSquare size={12}/> Adicionar</>}
                </button>
              </div>
            </div>
          )}
          {updates.length === 0 ? (
            <div style={{ textAlign:'center', padding:32, color:'var(--gray-400)', fontSize:13 }}>
              Sem comentários ainda.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {updates.map((upd, i) => (
                <div key={upd.update_id} style={{ display:'flex', gap:12,
                  paddingBottom:12, marginBottom:12,
                  borderBottom: i < updates.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                  {/* Avatar */}
                  <div style={{ flexShrink:0 }}>
                    <div style={{ width:30, height:30, borderRadius:'50%',
                      background: statusInfo.color, display:'flex', alignItems:'center',
                      justifyContent:'center', fontSize:12, fontWeight:700, color:'white' }}>
                      {(upd.created_by_name || '?')[0].toUpperCase()}
                    </div>
                  </div>
                  {/* Content */}
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'baseline', marginBottom:6 }}>
                      <span style={{ fontSize:13, fontWeight:600,
                        color:'var(--gray-800)' }}>{upd.created_by_name}</span>
                      <span style={{ fontSize:11, color:'var(--gray-400)' }}>
                        {new Date(upd.created_at).toLocaleString('pt-PT')}
                      </span>
                    </div>
                    <div style={{ fontSize:13, color:'var(--gray-700)',
                      lineHeight:1.6,
                      background:'white', border:'1px solid var(--gray-200)',
                      borderRadius:6, padding:'8px 12px' }}
                      dangerouslySetInnerHTML={{ __html: upd.update_text || '' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ CONCLUSÃO ════════════════════════════════════════════════════ */}
      {!isNew && tab==='conclusion' && (
        <div className="form-section">
          {!isClosed && (
            <div style={{ padding:'12px 16px', background:'#fffbeb',
              border:'1px solid #fcd34d', borderRadius:8, marginBottom:16,
              fontSize:13, color:'#92400e' }}>
              ⚠️ Esta secção destina-se a planos concluídos ou cancelados.
              Pode preencher agora mas os dados fazem mais sentido ao fechar o plano.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Data de Conclusão</label>
              <input type="date" className="form-control ap-date-input-md"
                value={form.completed_date || ''}
                onChange={e => setF('completed_date', e.target.value)}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Avaliação da Eficácia</label>
              <RichTextEditor
                value={form.effectiveness_eval || ''}
                onChange={html => setF('effectiveness_eval', html)}
                placeholder="Resultado obtido, evidências, se o problema foi resolvido…"
                minHeight={120}
              />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Lições Aprendidas</label>
              <RichTextEditor
                value={form.lessons_learned || ''}
                onChange={html => setF('lessons_learned', html)}
                placeholder="O que aprendemos? O que fazer diferente da próxima vez?…"
                minHeight={120}
              />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Ação Eficaz?</label>
              <div style={{ display:'flex', gap:8, paddingTop:4 }}>
                {[{v:true,l:'✅ Sim'},{v:false,l:'❌ Não'}].map(o => (
                  <button key={String(o.v)} type="button"
                    onClick={() => setF('is_effective', o.v)}
                    style={{ padding:'5px 16px', borderRadius:20, cursor:'pointer',
                      fontSize:13, fontWeight: form.is_effective===o.v ? 700 : 400,
                      border: `1.5px solid ${form.is_effective===o.v ? 'var(--primary)' : 'var(--gray-300)'}`,
                      background: form.is_effective===o.v ? '#e0f2f1' : 'white',
                      color: form.is_effective===o.v ? 'var(--primary)' : 'var(--gray-600)' }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
            {can('action_plans', 'write') && (
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <FiSave size={14}/> {saving ? 'A guardar…' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Task Modal */}
      {taskModal !== null && (
        <ActionPlanTaskModal
          task={taskModal?.public_id ? taskModal : null}
          users={users}
          pendingFiles={pendingActionFiles[taskModal?.public_id || 'new'] || []}
          onPendingFilesChange={files =>
            setPendingActionFiles(p => ({ ...p, [taskModal?.public_id || 'new']: files }))
          }
          onSave={handleTaskModalSave}
          onCancel={() => setTaskModal(null)}
        />
      )}

      {/* ── Audit Log — Plan level ─────────────────────────────────────── */}
      {!isNew && showAuditLog && (
        <AuditLogPanel
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          entityType="action_plan"
          entityId={plan?.action_plan_id?.toString()}
          title={`Histórico — ${plan?.reference_no} ${plan?.title ? '· ' + plan.title : ''}`}
        />
      )}

      {/* ── Audit Log — Task level ───────────────────────────────────────── */}
      {auditAction && (
        <AuditLogPanel
          isOpen={!!auditAction}
          onClose={() => setAuditAction(null)}
          entityType="action_plan_action"
          entityId={auditAction?.action_id?.toString()}
          title={`Histórico tarefa — ${auditAction?.description?.replace(/<[^>]*>/g,'').slice(0,50) || 'Tarefa'}`}
        />
      )}

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>

    </div>
  );
};

export default ActionPlanDetail;